import { useCallback, useState } from 'react';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { SETTINGS_PAGE } from '../settings-shared';
import { useChangePasswordEligibility } from '../settings-queries';

export interface ChangePasswordEntry {
  /** Whether to render the entry point at all. */
  visible: boolean;
  open: boolean;
  show: () => void;
  hide: () => void;
}

/**
 * The Settings entry point for changing the password: whether to offer it, and the modal's
 * open/closed state. Shared by the desktop row and the mobile section so the gate can't drift.
 *
 * The verdict is the server's, computed from all three credential facts together — see
 * `lib/change-password-eligibility.ts` for the rule (a Wix password must be the account's ONLY way in)
 * and for why it cannot be scoped to how the live session authenticated. Unknown counts as hidden:
 * while the check is in flight, and if it fails, the row does not exist.
 */
export function useChangePasswordEntry(): ChangePasswordEntry {
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const eligibility = useChangePasswordEligibility();
  useLoadBi('change-password-eligibility', { isLoading: eligibility.isLoading, isError: eligibility.isError });

  const show = useCallback(() => {
    logPageClickBi({ ...SETTINGS_PAGE, button_name: 'change_password' });
    setOpen(true);
  }, [logPageClickBi]);

  return {
    visible: eligibility.data === true,
    open,
    show,
    hide: useCallback(() => setOpen(false), []),
  };
}
